import { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { ArrowLeft, Send, Loader2 } from 'lucide-react';
import { formatDate } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MessagesPage() {
  const { tradeRequestId } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [selectedConversation, setSelectedConversation] = useState(null);
  
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchConversations();
  }, []);

  useEffect(() => {
    if (tradeRequestId) {
      fetchMessages(tradeRequestId);
    }
  }, [tradeRequestId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchConversations = async () => {
    try {
      const response = await axios.get(`${API}/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations(response.data);
      
      // If we have a tradeRequestId, find and set that conversation
      if (tradeRequestId) {
        const conv = response.data.find(c => c.trade_request_id === tradeRequestId);
        setSelectedConversation(conv || null);
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (requestId) => {
    try {
      const response = await axios.get(`${API}/messages/${requestId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !tradeRequestId) return;

    setSending(true);
    try {
      const response = await axios.post(
        `${API}/messages`,
        {
          trade_request_id: tradeRequestId,
          content: newMessage.trim()
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setMessages(prev => [...prev, response.data]);
      setNewMessage('');
      fetchConversations(); // Refresh to update last message
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setSending(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const selectConversation = (conv) => {
    setSelectedConversation(conv);
    navigate(`/messages/${conv.trade_request_id}`);
  };

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-64px)] bg-[#F9F9F7] animate-fadeIn" data-testid="messages-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-2xl border border-stone-100 overflow-hidden" style={{ height: 'calc(100vh - 180px)' }}>
          <div className="flex h-full">
            {/* Conversations List */}
            <div className={`w-full md:w-80 border-r border-stone-100 flex-shrink-0 ${tradeRequestId ? 'hidden md:block' : ''}`}>
              <div className="p-4 border-b border-stone-100">
                <h2 className="font-semibold text-stone-900">Messages</h2>
              </div>
              <ScrollArea className="h-[calc(100%-60px)]">
                {conversations.length === 0 ? (
                  <div className="p-6 text-center">
                    <p className="text-stone-500 text-sm">No conversations yet</p>
                    <p className="text-stone-400 text-xs mt-1">
                      Send a trade request to start messaging
                    </p>
                  </div>
                ) : (
                  <div className="divide-y divide-stone-100">
                    {conversations.map((conv) => (
                      <button
                        key={conv.trade_request_id}
                        onClick={() => selectConversation(conv)}
                        className={`w-full p-4 text-left hover:bg-stone-50 transition-colors ${
                          tradeRequestId === conv.trade_request_id ? 'bg-stone-50' : ''
                        }`}
                        data-testid={`conversation-${conv.trade_request_id}`}
                      >
                        <div className="flex gap-3">
                          <div className="w-12 h-12 rounded-full bg-[#008080] text-white flex items-center justify-center flex-shrink-0">
                            {conv.other_user_name?.charAt(0).toUpperCase()}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex justify-between items-start">
                              <p className="font-medium text-stone-900 truncate">
                                {conv.other_user_name}
                              </p>
                              <span className="text-xs text-stone-400 flex-shrink-0 ml-2">
                                {formatDate(conv.last_message_at)}
                              </span>
                            </div>
                            <p className="text-sm text-stone-500 truncate mt-0.5">
                              {conv.last_message || 'Trade request sent'}
                            </p>
                            <p className="text-xs text-stone-400 mt-1 truncate">
                              {conv.sender_listing?.title} ↔ {conv.receiver_listing?.title}
                            </p>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </div>

            {/* Chat Area */}
            <div className={`flex-1 flex flex-col ${!tradeRequestId ? 'hidden md:flex' : ''}`}>
              {tradeRequestId && selectedConversation ? (
                <>
                  {/* Chat Header */}
                  <div className="p-4 border-b border-stone-100 flex items-center gap-3">
                    <button
                      onClick={() => navigate('/messages')}
                      className="md:hidden p-2 -ml-2 hover:bg-stone-100 rounded-lg"
                    >
                      <ArrowLeft className="w-5 h-5" />
                    </button>
                    <div className="w-10 h-10 rounded-full bg-[#008080] text-white flex items-center justify-center">
                      {selectedConversation.other_user_name?.charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-stone-900">{selectedConversation.other_user_name}</p>
                      <p className="text-xs text-stone-500 truncate">
                        Trading: {selectedConversation.sender_listing?.title} ↔ {selectedConversation.receiver_listing?.title}
                      </p>
                    </div>
                  </div>

                  {/* Messages */}
                  <ScrollArea className="flex-1 p-4">
                    <div className="space-y-4">
                      {messages.length === 0 ? (
                        <div className="text-center py-8">
                          <p className="text-stone-500">No messages yet</p>
                          <p className="text-sm text-stone-400 mt-1">Send a message to start the conversation</p>
                        </div>
                      ) : (
                        messages.map((msg) => (
                          <div
                            key={msg.id}
                            className={`flex ${
                              msg.sender_id === 'system' || msg.is_system 
                                ? 'justify-center' 
                                : msg.sender_id === user.id 
                                  ? 'justify-end' 
                                  : 'justify-start'
                            }`}
                          >
                            {msg.sender_id === 'system' || msg.is_system ? (
                              <div className="px-4 py-2 bg-stone-100 rounded-full text-sm text-stone-600 max-w-md text-center">
                                {msg.content}
                              </div>
                            ) : (
                              <div
                                className={`message-bubble px-4 py-2.5 ${
                                  msg.sender_id === user.id ? 'sent' : 'received'
                                }`}
                                data-testid={`message-${msg.id}`}
                              >
                                <p className="text-sm">{msg.content}</p>
                                <p className={`text-xs mt-1 ${
                                  msg.sender_id === user.id ? 'text-white/70' : 'text-stone-400'
                                }`}>
                                  {formatDate(msg.created_at)}
                                </p>
                              </div>
                            )}
                          </div>
                        ))
                      )}
                      <div ref={messagesEndRef} />
                    </div>
                  </ScrollArea>

                  {/* Message Input */}
                  <div className="p-4 border-t border-stone-100">
                    <form onSubmit={sendMessage} className="flex gap-2">
                      <Input
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder="Type a message..."
                        className="flex-1 h-12 rounded-xl"
                        disabled={sending}
                        data-testid="message-input"
                      />
                      <Button
                        type="submit"
                        disabled={!newMessage.trim() || sending}
                        className="h-12 w-12 rounded-xl bg-[#008080] hover:bg-[#008080]/90"
                        data-testid="send-message-btn"
                      >
                        {sending ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <Send className="w-5 h-5" />
                        )}
                      </Button>
                    </form>
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center">
                    <div className="w-16 h-16 rounded-full bg-stone-100 flex items-center justify-center mx-auto mb-4">
                      <Send className="w-8 h-8 text-stone-400" />
                    </div>
                    <p className="text-stone-600">Select a conversation</p>
                    <p className="text-sm text-stone-400 mt-1">Choose from the list to start messaging</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
